import { useCallback, useState } from "react";
import useSpeechToText from "../../hooks/useSpeechToText";

/**
 * Flujo de dictado por voz con refinado por IA y revisión antes de insertar.
 *
 * Fases: "idle" -> "listening" -> "refining" -> "review" -> "idle".
 * El texto solo llega al editor cuando el médico pulsa Insertar: la IA no
 * escribe directamente en la receta o la nota.
 *
 * @param {object} options
 * @param {string} options.language idioma de la interfaz ("es" | "en")
 * @param {(rawText: string) => Promise<string>} options.refine llama a la IA
 * @param {(text: string) => void} options.onInsert agrega el texto aprobado
 */
export default function useDictation({ language = "es", refine, onInsert }) {
  const [phase, setPhase] = useState("idle");
  const [rawText, setRawText] = useState("");
  const [refinedText, setRefinedText] = useState("");
  const [refineError, setRefineError] = useState(null);

  const handleFinalResult = useCallback(
    async (text) => {
      if (!text || !text.trim()) {
        setPhase("idle");
        return;
      }
      setRawText(text);
      setRefinedText("");
      setRefineError(null);
      setPhase("refining");
      try {
        const refined = await refine(text);
        setRefinedText((refined || "").trim() || text);
      } catch (error) {
        console.error("AI refinement failed, using raw text:", error);
        setRefinedText(text);
        setRefineError(
          error?.response?.status === 403 ? "ai-disabled" : "ai-failed"
        );
      } finally {
        setPhase("review");
      }
    },
    [refine]
  );

  const speech = useSpeechToText({
    language,
    onFinalResult: handleFinalResult,
  });

  const start = useCallback(() => {
    setRawText("");
    setRefinedText("");
    setRefineError(null);
    speech.startListening();
    setPhase("listening");
  }, [speech]);

  const stop = useCallback(() => speech.stopListening(), [speech]);

  const reset = useCallback(() => {
    setPhase("idle");
    setRawText("");
    setRefinedText("");
    setRefineError(null);
  }, []);

  const cancel = useCallback(() => {
    speech.cancelListening();
    reset();
  }, [speech, reset]);

  const insert = useCallback(
    (text) => {
      const clean = (text || "").trim();
      if (clean) onInsert(clean);
      reset();
    },
    [onInsert, reset]
  );

  // Si el navegador corta el dictado sin texto o con error, se vuelve a idle.
  const effectivePhase =
    phase === "listening" && !speech.isListening && !!speech.error
      ? "idle"
      : phase;

  return {
    phase: effectivePhase,
    rawText,
    refinedText,
    setRefinedText,
    refineError,
    transcript: speech.transcript,
    interimText: speech.interimText,
    startedAt: speech.startedAt,
    isSupported: speech.isSupported,
    speechError: speech.error,
    clearSpeechError: speech.clearError,
    start,
    stop,
    cancel,
    insert,
    discard: reset,
  };
}
